/**
 * Priority levels for modals in the queue
 * Higher values = higher priority (shown first)
 */
export enum ModalPriority {
  LOW = 1,
  NORMAL = 5,
  HIGH = 10,
  CRITICAL = 20,
  HIGHEST = 30,
  SAFETY_SYSTEM = 40,
}

import { type ModalId } from '@heidi/schema/ml-scribe';

/**
 * A **local-regime** modal id (OpenSpec `monorepo-shell-registry` design D4):
 * `ModalId` is generated from the backend OpenAPI schema and must not be
 * hand-edited, and the Kinde→Auth0 migration modal id is not in the backend
 * enum, so a client-side constant is cast.
 *
 * Safe because the modal is enqueued with `showOnce: false` — it never
 * consults the backend-synced `seenModalIds` set, and leaves the queue via
 * launch-atom recompute — so no non-enum id is POSTed to the backend. Replace
 * with `ModalId.KINDE_MIGRATION_MODAL` once the backend adds it and the schema
 * is regenerated (the prechart ids retired their casts exactly that way, S3);
 * the id STRING is frozen and never changes with it.
 * See openspec/changes/kinde-auth0-migration-prompt (Task 2.1).
 */
export const KINDE_MIGRATION_MODAL_ID = 'KINDE_MIGRATION_MODAL' as ModalId;

/**
 * Configuration for a queued modal
 * Each modal component subscribes to the queue to determine if it should be open
 */
export interface QueuedModal {
  /** Unique identifier for the modal */
  id: ModalId;

  /** Priority level using ModalPriority enum - higher values are shown first */
  priority: ModalPriority;

  /** Whether the modal can be dismissed by clicking outside or pressing ESC */
  dismissible?: boolean;

  /** Whether this modal should only be shown after user creation (determined by whether they have visited
   * the new-user route this session) */
  showOnlyAfterAccountCreation?: boolean;

  /** Whether this modal should only be shown once (tracked via seenModalIds)
   * Default: false (modals can be shown multiple times) */
  showOnce?: boolean;

  // Skips only R2 for the managed-release invite; all other tier rules apply.
  // Named exception: `.ai-context/modal-priority-stack.md`.
  enterpriseLockExempt?: boolean;
}
